import * as moment from 'moment';

export interface HormigonDispatchSelectors<T> {
  deliveryDate(row: T): string;
  status(row: T): number;
  flagPending(row: T): string;
  startTime(row: T): string;
  identifier(row: T): string | number;
}

export interface HormigonDispatchSelection<T> {
  current: T | null;
  currentConflicts: T[];
  others: T[];
}

/**
 * Clasifica los viajes de Hormigón Mixer según las reglas operacionales.
 *
 * `current` es el único viaje que se renderiza en la tarjeta principal.
 * `currentConflicts` conserva cualquier registro adicional que incumpla la
 * expectativa de unicidad, sin degradarlo a "Otros despachos".
 */
export function classifyHormigonDispatches<T>(
  rows: readonly T[],
  selectors: HormigonDispatchSelectors<T>,
  today = moment(),
): HormigonDispatchSelection<T> {
  const isToday = (row: T): boolean =>
    moment(selectors.deliveryDate(row), 'YYYY-MM-DD', true).isSame(today, 'day');

  const currentCandidates = rows
    .filter((row) => {
      const status = Number(selectors.status(row));
      return isToday(row) && status < 6 && status !== 0;
    })
    .sort((left, right) => {
      const timeComparison = compareTimes(
        selectors.startTime(left),
        selectors.startTime(right),
      );
      if (timeComparison !== 0) return timeComparison;
      return compareIdentifiers(
        selectors.identifier(left),
        selectors.identifier(right),
      );
    });

  return {
    current: currentCandidates[0] ?? null,
    currentConflicts: currentCandidates.slice(1),
    others: rows.filter(
      (row) => !isToday(row) && selectors.flagPending(row) === '1',
    ),
  };
}

/**
 * Despacho actual, compartido por el dashboard y la página de despacho actual.
 * Los viajes con guía firmada nunca se consideran despacho actual.
 */
export function selectCurrentHormigonDispatch<T>(
  rows: readonly T[],
  selectors: HormigonDispatchSelectors<T>,
  isSigned: (row: T) => boolean,
  today = moment(),
): Pick<HormigonDispatchSelection<T>, 'current' | 'currentConflicts'> {
  const pending = (rows || []).filter((row) => !isSigned(row));
  const { current, currentConflicts } = classifyHormigonDispatches(pending, selectors, today);
  return { current, currentConflicts };
}

function compareTimes(left: string, right: string): number {
  const leftMinutes = toMinutes(left);
  const rightMinutes = toMinutes(right);
  return leftMinutes - rightMinutes;
}

function toMinutes(value: string): number {
  const match = String(value ?? '').match(/^(\d{1,2}):(\d{2})/);
  if (!match) return Number.POSITIVE_INFINITY;
  return Number(match[1]) * 60 + Number(match[2]);
}

function compareIdentifiers(left: string | number, right: string | number): number {
  return String(left ?? '').localeCompare(String(right ?? ''), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}
